import { Typography } from '@/components/common';
import { PageContainer, SimpleBanner } from '@/components/landing';
import StateRightsCard from './StateRightsCard';
import { consumerRightsByState } from './ComsumerRightsByState';
import Link from 'next/link';

export const PrivacyPolicy = () => {
	return (
		<>
			<SimpleBanner
				text="Privacy Policy"
				description={
					<div className="flex items-center gap-1">
						<Typography type="semibold-16" className="text-black">
							Effective date:{' '}
						</Typography>
						<Typography type="regular-16">06/29/2026</Typography>
					</div>
				}
			/>
			<PageContainer className="pb-16 lg:pb-[88px]">
				<Typography type="regular-16">
					This privacy policy describes how Loch Brandy LLC, d/b/a Claim Magic,
					(&quot;Claim Magic,&quot; &quot;Company,&quot; &quot;we,&quot; or
					&quot;us&quot;) collects and processes personal information about our
					consumers (the &quot;Privacy Policy&quot;). This Privacy Policy
					provides information regarding our collection and use of personal
					information.
				</Typography>
				<br />
				<Typography type="regular-16">
					Please read the Privacy Policy carefully. When you submit information
					to or through Claim Magic, you consent to the collection and
					processing of your information as described in this Privacy Policy. If
					you do not agree to the Privacy Policy, you may not access or use the
					Claim Magic platform.
				</Typography>
				<br />
				<Typography type="regular-16">
					Claim Magic voluntarily provides the disclosures and rights described
					in this Privacy Policy as a matter of best practice and consumer
					transparency. This Privacy Policy is modeled after the
					consumer-protection principles found in the California Consumer
					Protection Act (&quot;CCPA&quot;) and adopts the defined terms
					outlined therein. Nothing in this Privacy Policy, however, constitutes
					an admission or representation that Claim Magic is a
					&apos;business&apos; subject to the CCPA, or that any individual is a
					&apos;consumer&apos; entitled to enforce rights under that statute. To
					the extent the CCPA does not apply to us or to you, the disclosures
					and rights described herein are provided voluntarily and do not create
					any legal obligation or right of action.
				</Typography>
				<br />
				<br />
				<Typography type="regular-16" className="text-purple-150 font-semibold">
					Personal Information Collected
				</Typography>
				<br />
				<Typography type="regular-16">
					We collect and use information that identifies, relates to, describes,
					references, is reasonably capable of being associated with, or could
					reasonably be linked, directly or indirectly, with a particular
					consumer or household (&quot;personal information&quot;). Personal
					information does not include:
				</Typography>
				<ul className="list-disc ml-7 mt-4 space-y-2">
					<li>
						<Typography type="regular-16">
							Publicly available information, including from government records,
							through widely distributed media, or that the consumer made
							publicly available without restricting it to a specific audience.
						</Typography>
					</li>
					<li>
						<Typography type="regular-16">
							Lawfully obtained, truthful information that is a matter of public
							concern.
						</Typography>
					</li>
					<li>
						<Typography type="regular-16">
							Deidentified or aggregated consumer information.
						</Typography>
					</li>
				</ul>
				<br />
				<Typography type="semibold-16">
					Personal Information Categories Chart
				</Typography>
				<Typography type="regular-16" className="mt-2 mb-4">
					The chart below identifies the categories of personal information we
					collected from our consumers within the last 12 months and the
					expected retention period.
				</Typography>

				<div className="overflow-x-auto mb-8">
					<table className="min-w-full border-collapse border border-gray-300">
						<thead>
							<tr className="bg-gray-100">
								<th className="border border-gray-300 p-2 text-left">
									Category
								</th>
								<th className="border border-gray-300 p-2 text-left">
									Examples
								</th>
								<th className="border border-gray-300 p-2 text-left">
									Collected
								</th>
								<th className="border border-gray-300 p-2 text-left">
									Retention Period
								</th>
							</tr>
						</thead>
						<tbody>
							<tr>
								<td className="border border-gray-300 p-2 align-top font-semibold">
									A. Identifiers
								</td>
								<td className="border border-gray-300 p-2 align-top">
									Name, alias, postal address, unique personal identifier,
									online identifier, IP address, email address, account name,
									driver’s license number, or other similar identifiers.
								</td>
								<td className="border border-gray-300 p-2 align-top">YES</td>
								<td className="border border-gray-300 p-2 align-top">
									As long as necessary for (1) the purpose collected; (2) as
									required by law; (3) to pursue and defend legal claims.
								</td>
							</tr>
							<tr>
								<td className="border border-gray-300 p-2 align-top font-semibold">
									B. Personal information categories listed in the California
									Customer Records statute (Cal. Civ. Code § 1798.80(e))
								</td>
								<td className="border border-gray-300 p-2 align-top">
									Name, signature, address, telephone number, driver’s license
									or state identification card number, education, employment,
									employment history, credit card number, debit card number, or
									any other financial information, medical information, or
									health insurance information. Some personal information
									included in this category may overlap with other categories.
								</td>
								<td className="border border-gray-300 p-2 align-top">YES</td>
								<td className="border border-gray-300 p-2 align-top">
									As long as necessary for (1) the purpose collected; (2) as
									required by law; (3) to pursue and defend legal claims.
								</td>
							</tr>
							<tr>
								<td className="border border-gray-300 p-2 align-top font-semibold">
									C. Protected classification characteristics
								</td>
								<td className="border border-gray-300 p-2 align-top">
									Age (40 years or older) or marital status.
								</td>
								<td className="border border-gray-300 p-2 align-top">YES</td>
								<td className="border border-gray-300 p-2 align-top">
									As long as necessary for (1) the purpose collected; (2) as
									required by law; (3) to pursue and defend legal claims.
								</td>
							</tr>
							<tr>
								<td className="border border-gray-300 p-2 align-top font-semibold">
									D. Commercial information
								</td>
								<td className="border border-gray-300 p-2 align-top">
									Records of personal property, products or services purchased,
									obtained, or considered, or other purchasing or consuming
									histories or tendencies.
								</td>
								<td className="border border-gray-300 p-2 align-top">YES</td>
								<td className="border border-gray-300 p-2 align-top">
									As long as necessary for (1) the purpose collected; (2) as
									required by law; (3) to pursue and defend legal claims.
								</td>
							</tr>
							<tr>
								<td className="border border-gray-300 p-2 align-top font-semibold">
									E. Internet or other similar network activity
								</td>
								<td className="border border-gray-300 p-2 align-top">
									Browsing history, search history, information on a consumer’s
									interaction with a website, application, or advertisement.
								</td>
								<td className="border border-gray-300 p-2 align-top">YES</td>
								<td className="border border-gray-300 p-2 align-top">
									As long as necessary for (1) the purpose collected; (2) as
									required by law; (3) to pursue and defend legal claims.
								</td>
							</tr>
							<tr>
								<td className="border border-gray-300 p-2 align-top font-semibold">
									F. Professional or employment-related information
								</td>
								<td className="border border-gray-300 p-2 align-top">
									Current or past job history.
								</td>
								<td className="border border-gray-300 p-2 align-top">YES</td>
								<td className="border border-gray-300 p-2 align-top">
									As long as necessary for (1) the purpose collected; (2) as
									required by law; (3) to pursue and defend legal claims.
								</td>
							</tr>
							<tr>
								<td className="border border-gray-300 p-2 align-top font-semibold">
									G. Sensitive personal information
								</td>
								<td className="border border-gray-300 p-2 align-top">
									Further identified in the Sensitive Personal Information
									Categories Chart below.
								</td>
								<td className="border border-gray-300 p-2 align-top">
									YES — see SPI chart below
								</td>
								<td className="border border-gray-300 p-2 align-top">
									As long as necessary for (1) the purpose collected; (2) as
									required by law; (3) to pursue and defend legal claims.
								</td>
							</tr>
						</tbody>
					</table>
				</div>

				<Typography type="semibold-16">
					Sensitive Personal Information Categories Chart
				</Typography>
				<Typography type="regular-16" className="mt-2 mb-4">
					Sensitive personal information is a subtype of personal information
					consisting of the specific information categories listed in the chart
					below. Importantly, the CCPA only treats this information as sensitive
					personal information when we collect or use it to infer
					characteristics about a consumer.
					<br />
					<br />
					The chart below identifies which sensitive personal information
					categories, if any, we have collected from consumers to infer
					characteristics about them in the last 12 months.
				</Typography>

				<div className="overflow-x-auto mb-8">
					<table className="min-w-full border-collapse border border-gray-300">
						<thead>
							<tr className="bg-gray-100">
								<th className="border border-gray-300 p-2 text-left">
									Sensitive Personal Information Category
								</th>
								<th className="border border-gray-300 p-2 text-left">
									Description & How We Use It
								</th>
								<th className="border border-gray-300 p-2 text-left">
									Collected?
								</th>
							</tr>
						</thead>
						<tbody>
							<tr>
								<td className="border border-gray-300 p-2 align-top font-semibold">
									L.1. Complete account access credentials (usernames, account
									logins, passwords)
								</td>
								<td className="border border-gray-300 p-2 align-top">
									Collected to secure your Claim Magic account. Not shared with
									third parties except as necessary to provide account services.
								</td>
								<td className="border border-gray-300 p-2 align-top">YES</td>
							</tr>
							<tr>
								<td className="border border-gray-300 p-2 align-top font-semibold">
									L.2. Contents of mail, email, or text messages not directed to
									us
								</td>
								<td className="border border-gray-300 p-2 align-top">
									Documents and correspondence you upload may contain email or
									mail content. We process this content solely to assess your
									claim eligibility.
								</td>
								<td className="border border-gray-300 p-2 align-top">YES</td>
							</tr>
							<tr>
								<td className="border border-gray-300 p-2 align-top font-semibold">
									L.3. Health or medical information
								</td>
								<td className="border border-gray-300 p-2 align-top">
									Documents you submit may contain health or insurance
									information. We process this content solely to assess and
									submit your claim.
								</td>
								<td className="border border-gray-300 p-2 align-top">YES</td>
							</tr>
						</tbody>
					</table>
				</div>

				<Typography type="semibold-16">
					Sources of Personal Information
				</Typography>
				<Typography type="regular-16" className="mt-2">
					We obtain the categories of personal information listed above from the
					following categories of sources:
				</Typography>
				<ul className="list-disc ml-7 mt-2 space-y-2 mb-8">
					<li>
						<Typography type="regular-16">
							Directly from you, such as when you create an account, fill out
							forms, upload claim documentation, or submit queries through the
							chat function.
						</Typography>
					</li>
					<li>
						<Typography type="regular-16">
							Indirectly from you, such as from your interactions with the Claim
							Magic website, mobile application, and advertisements for our
							services (including through Google Analytics and the Adquire pixel
							and similar tracking technologies installed on the Claim Magic
							website).
						</Typography>
					</li>
					<li>
						<Typography type="regular-16">
							From our service providers, such as fraud detection providers,
							data analytics providers, advertising networks, and internet
							service providers.
						</Typography>
					</li>
					<li>
						<Typography type="regular-16">
							From business partners who may provide services you have
							requested.
						</Typography>
					</li>
				</ul>

				<Typography type="regular-16" className="text-purple-150 font-semibold">
					How We Use Personal Information
				</Typography>
				<br />
				<Typography type="semibold-16">
					Personal Information Collection, Use, and Disclosure Purposes
				</Typography>
				<Typography type="regular-16" className="mt-2">
					We may use and disclose the personal information we collect to advance
					Claim Magic&apos;s business and commercial purposes, specifically to:
				</Typography>
				<ul className="list-disc ml-7 mt-2 space-y-2 mb-8">
					<li>
						<Typography type="regular-16">
							Fulfill the reasons you provided the personal information,
							including assessing your eligibility for claims and submitting
							claims in legal matters on your behalf with arbitration forums
							and/or state or federal courts.
						</Typography>
					</li>
					<li>
						<Typography type="regular-16">
							Communicate with third-parties who may assist in preparing your
							claims for arbitration or to file in court.
						</Typography>
					</li>
					<li>
						<Typography type="regular-16">
							Communicate with you and customize your experience, including by
							sending you case updates or emails, and addressing queries within
							the Claim Magic platform.
						</Typography>
					</li>
					<li>
						<Typography type="regular-16">
							Review and assess your eligibility, including summarizing and
							analyzing documentation you submit using AI-assisted tools (OpenAI
							API).
						</Typography>
					</li>
					<li>
						<Typography type="regular-16">
							Advertise our services, including by sending marketing materials,
							advertisements, as well as to assess the effectiveness of
							advertising and other marketing activities.
						</Typography>
					</li>
					<li>
						<Typography type="regular-16">
							Create, maintain, customize, and secure your account with Claim
							Magic.
						</Typography>
					</li>
					<li>
						<Typography type="regular-16">
							Maintain and improve our services, including using usage
							information to address issues in how our website and application
							function.
						</Typography>
					</li>
					<li>
						<Typography type="regular-16">
							Analyze trends and gather statistical information.
						</Typography>
					</li>
					<li>
						<Typography type="regular-16">
							Prevent fraud and perform identity verification.
						</Typography>
					</li>
					<li>
						<Typography type="regular-16">
							Engage in corporate transactions requiring review of consumer
							records, such as for evaluating potential Company mergers and
							acquisitions.
						</Typography>
					</li>
					<li>
						<Typography type="regular-16">
							Comply with all applicable laws and regulations.
						</Typography>
					</li>
					<li>
						<Typography type="regular-16">
							Exercise or defend the legal rights of Claim Magic and its
							employees, affiliates, customers, contractors, and agents.
						</Typography>
					</li>
					<li>
						<Typography type="regular-16">
							Respond to law enforcement requests and as required by applicable
							law or court order.
						</Typography>
					</li>
				</ul>

				<Typography type="semibold-16">
					Sensitive Personal Information Use and Disclosure Purposes
				</Typography>
				<Typography type="regular-16" className="mt-2">
					We may use or disclose sensitive personal information for the
					following statutorily approved reasons (Permitted SPI Purposes):
				</Typography>
				<ul className="list-disc ml-7 mt-2 space-y-2 mb-4">
					<li>
						<Typography type="regular-16">
							Performing actions that are necessary for our consumer
							relationship and that an average consumer in a relationship with
							us would reasonably expect.
						</Typography>
					</li>
					<li>
						<Typography type="regular-16">
							Preventing, detecting, and investigating security incidents that
							compromise the availability, authenticity, integrity, or
							confidentiality of stored or transmitted personal information.
						</Typography>
					</li>
					<li>
						<Typography type="regular-16">
							Defending against and prosecuting those responsible for malicious,
							deceptive, fraudulent, or illegal actions directed at the Company.
						</Typography>
					</li>
					<li>
						<Typography type="regular-16">Ensuring physical safety.</Typography>
					</li>
					<li>
						<Typography type="regular-16">
							Short-term, transient use, such as non-personalized advertising
							shown as part of your current interactions with us, where we do
							not disclose the sensitive personal information to another third
							party or use it to build a profile about you outside your current
							interaction.
						</Typography>
					</li>
					<li>
						<Typography type="regular-16">
							Services performed for Claim Magic, including maintaining or
							servicing accounts, processing or fulfilling transactions,
							verifying consumer information, processing payments, and providing
							analytic services.
						</Typography>
					</li>
					<li>
						<Typography type="regular-16">
							Collecting or processing sensitive personal information that we do
							not use for the purpose of inferring characteristics about a
							consumer.
						</Typography>
					</li>
				</ul>
				<Typography type="regular-16" className="mb-8">
					We <strong>do not</strong> use or disclose sensitive personal
					information for purposes other than the Permitted SPI Purposes
					described above.
				</Typography>

				<Typography type="semibold-16">
					Additional Categories or Other Purposes
				</Typography>
				<Typography type="regular-16" className="mt-2 mb-8">
					We will not collect additional categories of personal information or
					use the personal information we collected for materially different,
					unrelated, or incompatible purposes without providing you notice. If
					required by law, we will also seek your consent before using your
					personal information for a new or unrelated purpose.
					<br />
					<br />
					We may collect, process, and disclose aggregated or deidentified
					consumer information for any purpose, without restriction. When we
					collect, process, or disclose aggregated or deidentified consumer
					information, we will maintain and use it in deidentified form and will
					not attempt to reidentify the information, except to determine whether
					our deidentification processes satisfies any applicable legal
					requirements.
				</Typography>

				<Typography type="regular-16" className="text-purple-150 font-semibold">
					Disclosing, Selling, or Sharing Personal Information
				</Typography>
				<br />
				<Typography type="semibold-16">Business Purpose Disclosures</Typography>
				<Typography type="regular-16" className="mt-2 mb-4">
					We may disclose the personal information we collect to service
					providers and contractors for the business purposes described in the
					Personal Information Collection, Use, and Disclosure Purposes section
					and in the table below. We only make these business purpose
					disclosures under written contracts that describe the purposes,
					require the recipient to keep the personal information confidential,
					prohibit using the disclosed information for any purpose except
					performing the contract, and meet the CCPA&apos;s other contract
					requirements for engaging service providers or contractors.
					<br />
					<br />
					The chart below identifies the categories of entities to whom we have
					disclosed our consumers&apos; personal information for a business
					purpose over the preceding 12 months.
				</Typography>

				<div className="overflow-x-auto mb-8">
					<table className="min-w-full border-collapse border border-gray-300">
						<thead>
							<tr className="bg-gray-100">
								<th className="border border-gray-300 p-2 text-left">
									Category of Service Provider / Contractor
								</th>
								<th className="border border-gray-300 p-2 text-left">
									PI Categories Disclosed
								</th>
								<th className="border border-gray-300 p-2 text-left">
									Business Purpose
								</th>
							</tr>
						</thead>
						<tbody>
							<tr>
								<td className="border border-gray-300 p-2 align-top">
									IT developers and operating systems/platforms
								</td>
								<td className="border border-gray-300 p-2 align-top">
									A, B, C, D, E, L.1
								</td>
								<td className="border border-gray-300 p-2 align-top">
									To maintain and operate our website, mobile application, and
									infrastructure.
								</td>
							</tr>
							<tr>
								<td className="border border-gray-300 p-2 align-top">
									Payment processors and financial institutions
								</td>
								<td className="border border-gray-300 p-2 align-top">
									A, B, D, L.1
								</td>
								<td className="border border-gray-300 p-2 align-top">
									To process payments in connection with claim settlements,
									which may include your account number and routing number.
								</td>
							</tr>
							<tr>
								<td className="border border-gray-300 p-2 align-top">
									Internet service providers
								</td>
								<td className="border border-gray-300 p-2 align-top">A, E</td>
								<td className="border border-gray-300 p-2 align-top">
									To provide internet connectivity and related services.
								</td>
							</tr>
							<tr>
								<td className="border border-gray-300 p-2 align-top">
									Data analytics providers, including Google Analytics and
									Adquire
								</td>
								<td className="border border-gray-300 p-2 align-top">
									A, D, E
								</td>
								<td className="border border-gray-300 p-2 align-top">
									To analyze usage patterns, improve our services, and assess
									effectiveness of marketing.
								</td>
							</tr>
							<tr>
								<td className="border border-gray-300 p-2 align-top">
									Fraud detection and identity verification providers
								</td>
								<td className="border border-gray-300 p-2 align-top">A, B</td>
								<td className="border border-gray-300 p-2 align-top">
									To detect and prevent fraud and verify user identities.
								</td>
							</tr>
							<tr>
								<td className="border border-gray-300 p-2 align-top">
									Email marketing providers
								</td>
								<td className="border border-gray-300 p-2 align-top">A</td>
								<td className="border border-gray-300 p-2 align-top">
									To send newsletters, service updates, and marketing
									communications.
								</td>
							</tr>
							<tr>
								<td className="border border-gray-300 p-2 align-top">
									AI processing provider (OpenAI API)
								</td>
								<td className="border border-gray-300 p-2 align-top">
									A, C, D, F, L.2, L.3
								</td>
								<td className="border border-gray-300 p-2 align-top">
									To summarize and analyze documents and information you submit
									for claim eligibility assessment. Documents are not used to
									train OpenAI’s models per our data processing agreement.
								</td>
							</tr>
							<tr>
								<td className="border border-gray-300 p-2 align-top">
									Insurers and professional advisers
								</td>
								<td className="border border-gray-300 p-2 align-top">
									A, B, C, D, F, L.2, L.3
								</td>
								<td className="border border-gray-300 p-2 align-top">
									To assess and submit claims on your behalf.
								</td>
							</tr>
							<tr>
								<td className="border border-gray-300 p-2 align-top">
									Government entities
								</td>
								<td className="border border-gray-300 p-2 align-top">
									A, B, C, D
								</td>
								<td className="border border-gray-300 p-2 align-top">
									To comply with legal obligations and submit claims in
									applicable proceedings.
								</td>
							</tr>
						</tbody>
					</table>
				</div>

				<Typography type="semibold-16">
					Selling or Sharing Personal Information
				</Typography>
				<Typography type="regular-16" className="mt-2 mb-8">
					We do not sell your personal information for monetary consideration.
					However, we may share the following categories of personal information
					with advertising networks and analytics providers through tracking
					technologies: Identifiers (A) and Internet or electronic network
					activity (E). This may constitute &quot;selling&quot; under the CCPA.
				</Typography>

				<Typography type="regular-16" className="text-purple-150 font-semibold">
					Requests to Know, Delete, or Correct
				</Typography>
				<br />
				<Typography type="regular-16">
					To request to (including data portability), delete, or correct, please
					submit a verifiable request to us by either:
				</Typography>
				<ul className="list-disc ml-7 mt-2 space-y-2 mb-4">
					<li>
						<Typography type="regular-16">
							Emailing us at{' '}
							<a
								href="mailto:support@claimmagic.com"
								className="text-blue-link underline"
							>
								support@claimmagic.com
							</a>
						</Typography>
					</li>
					<li>
						<Typography type="regular-16">
							Visiting{' '}
							<a
								href="https://claimmagic.com/contact-us"
								className="text-blue-link underline"
							>
								https://claimmagic.com/contact-us
							</a>
						</Typography>
					</li>
				</ul>
				<Typography type="regular-16" className="mb-8">
					Please describe your request with sufficient detail so we can properly
					understand, evaluate, and respond to it. You or your authorized agent
					may only submit a request to know, including for data portability,
					twice in a 12-month period.
				</Typography>

				<Typography type="regular-16" className="text-purple-150 font-semibold">
					Request to Opt-Out
				</Typography>
				<br />
				<Typography type="regular-16">
					You can submit your request to opt-out through:
				</Typography>
				<ul className="list-disc ml-7 mt-2 space-y-2 mb-4">
					<li>
						<Typography type="regular-16">
							Emailing{' '}
							<a
								href="mailto:support@claimmagic.com"
								className="text-blue-link underline"
							>
								support@claimmagic.com
							</a>
						</Typography>
					</li>
				</ul>
				<Typography type="regular-16" className="mb-8">
					The Claim Magic website does not respond to opt-out preference
					signals, such as the Global Privacy Control (GPC).
				</Typography>

				<Typography type="semibold-16">
					Verification Process and Authorized Agents
				</Typography>
				<Typography type="regular-16" className="mt-2 mb-8">
					Only you, or someone legally authorized to act on your behalf, may
					make a request to know, delete, or correct related to your personal
					information. To designate an authorized agent, please provide a
					written and signed document by both you and the agent authorizing the
					agent to act on your behalf. You may also use a power of attorney.
					<br />
					<br />
					We cannot respond to your request to know, delete, or correct if we
					cannot verify your identity or authority to make the request. We will
					only use personal information provided in the request to verify the
					requestor&apos;s identity or authority to make the request.
					<br />
					<br />
					For requests to opt-out, we ask for the information necessary to
					complete the request, which may include, for example, the
					consumer&apos;s name, email address, or account username.
				</Typography>

				<Typography type="semibold-16">
					Responding to Your Requests to Know, Delete, or Correct
				</Typography>
				<Typography type="regular-16" className="mt-2 mb-8">
					We will confirm receipt of your request within fourteen (14) business
					days. If you do not receive confirmation within the ten-day timeframe,
					please contact{' '}
					<a
						href="mailto:support@claimmagic.com"
						className="text-blue-link underline"
					>
						support@claimmagic.com
					</a>
					.
					<br />
					<br />
					We endeavor to substantively respond to a verifiable request within 45
					days of its receipt. If we require more time (up to another 45 days),
					we will inform you of the reason and extension period in writing. We
					will deliver our written response to your verified email address.
					<br />
					<br />
					Any disclosures we provide will cover information for the 12-month
					period preceding the request&apos;s receipt date. We will consider
					requests to provide longer disclosure periods that do not extend past
					January 1, 2025, unless providing the longer timeframe would be
					impossible or involves disproportionate effort.
					<br />
					<br />
					We do not charge a fee to process or respond to your verifiable
					request unless it is excessive, repetitive, or manifestly unfounded.
				</Typography>

				<Typography type="semibold-16">
					Response and Timing on Request to Opt-Out
				</Typography>
				<Typography type="regular-16" className="mt-2 mb-8">
					In response to your request to opt-out, we will process your request,
					as soon as feasibly possible, but no later than 15 business days from
					the date we receive the request. We will only use personal information
					provided from your request to comply with the request.
					<br />
					<br />
					We will also notify our service providers, contractors, and certain
					other downstream recipients of your request to opt-out and instruct
					them to comply with your request and forward the request to their own
					downstream recipients, if applicable.
					<br />
					<br />
					Once you make a request to opt-out, we will wait at least 12 months
					before asking you to reauthorize personal information sharing.
					However, you may change your mind and opt back in at any time by
					contacting us at{' '}
					<a
						href="mailto:support@claimmagic.com"
						className="text-blue-link underline"
					>
						support@claimmagic.com
					</a>{' '}
					or via{' '}
					<Link
						href="https://claimmagic.com/contact-us"
						className="text-blue-link"
					>
						https://claimmagic.com/contact-us
					</Link>
				</Typography>

				<Typography type="regular-16" className="text-purple-150 font-semibold">
					How We Protect Your Personal Data
				</Typography>
				<br />
				<Typography type="regular-16" className="mb-8">
					We use commercially reasonable administrative, physical, and technical
					measures designed to protect your personal data from accidental loss
					or destruction and from unauthorized access, use, alteration, and
					disclosure. However, no website, mobile application, electronic
					storage, or online service is completely secure, and we cannot
					guarantee the security of your personal data transmitted to, through,
					using, or in connection with the Services.
					<br />
					<br />
					The safety and security of your information also depends on you. You
					are responsible for taking steps to protect your personal data against
					unauthorized use, disclosure, and access.
				</Typography>

				<Typography type="regular-16" className="text-purple-150 font-semibold">
					Privacy Policy Changes
				</Typography>
				<br />
				<Typography type="regular-16" className="mb-8">
					We reserve the right to update this Privacy Policy at any time. If we
					make any material changes to this Privacy Policy, we will update the
					policy&apos;s effective date and post the updated policy on our
					website at{' '}
					<Link href={'/'} className="text-blue-link">
						www.claimmagic.com
					</Link>
					. We encourage you to check our website to review the current Privacy
					Policy in effect.
				</Typography>

				<Typography type="regular-16" className="text-purple-150 font-semibold">
					Contact Information
				</Typography>
				<br />
				<Typography type="regular-16" className="mb-8">
					If you have any questions or comments about this policy, the ways in
					which we collect and use your information described here, your choices
					and rights regarding such use, or wish to exercise your rights under
					California law, please do not hesitate to contact us at:
					<br />
					<br />
					Website:{' '}
					<a
						href="https://claimmagic.com/contact-us"
						className="text-blue-link underline"
					>
						https://claimmagic.com/contact-us
					</a>
					<br />
					Email:{' '}
					<a
						href="mailto:support@claimmagic.com"
						className="text-blue-link underline"
					>
						support@claimmagic.com
					</a>
					<br />
					<br />
					If you need to access this Privacy Policy in an alternative format due
					to a disability, please contact support@claimmagic.com and include
					your preferred format and contact information.
				</Typography>

				<Typography type="semibold-16" className="uppercase">
					State By State Rights
				</Typography>
				<br />
				<div>
					{consumerRightsByState.map((item, index) => (
						<StateRightsCard consumerRightsByState={item} key={index} />
					))}
				</div>
			</PageContainer>
		</>
	);
};
